/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

/**
 * SegmentationService.js
 *
 * TensorFlow.js-based YOLO11n-seg segmentation for goat detection
 * From thesis yolotfjs.ts implementation
 *
 * Pipeline Step 1: Ziegenerkennung (Goat Detection)
 * Input: Image
 * Output: Binary segmentation mask + bounding box
 */

import * as tf from '@tensorflow/tfjs'
import '@tensorflow/tfjs-backend-webgpu'
import '@tensorflow/tfjs-backend-webgl'
import '@tensorflow/tfjs-backend-cpu'

/**
 * Bounding Box representation of xywh where xy is the center point of the bounding box
 */
export class Box {
  constructor(arr) {
    this.x = arr[0]
    this.y = arr[1]
    this.w = arr[2]
    this.h = arr[3]
  }

  topX() {
    return Math.ceil(this.x - this.w / 2)
  }

  topY() {
    return Math.ceil(this.y - this.h / 2)
  }

  // Properties for plain object compatibility
  get width() {
    return Math.ceil(this.w)
  }

  get height() {
    return Math.ceil(this.h)
  }

  width() {
    return Math.ceil(this.w)
  }

  height() {
    return Math.ceil(this.h)
  }

  // Getters for serialization compatibility
  get topXVal() {
    return this.x - this.w / 2
  }

  get topYVal() {
    return this.y - this.h / 2
  }
}

/**
 * Service for TensorFlow.js-based goat segmentation
 */
export class SegmentationService {
  constructor(config = {}) {
    this.config = {
      modelPath: '/ml/model.json',
      inputWidth: 640,
      inputHeight: 640,
      confidenceThreshold: 0.85, // Thesis threshold
      xyxy: 4,
      classes: 1,
      numMasks: 32,
      maskWidth: 160,
      maskHeight: 160,
      ...config,
    }

    this.model = null
    this.isInitialized = false
    this.debug = false
    this.currentBackend = null

    // State for current prediction
    this.originalWidth = null
    this.originalHeight = null
    this.scaledOriginalWidth = null
    this.scaledOriginalHeight = null
    this.box = null
    this.mask = null
    this.input = null
    this.inputImage = null
    this.output = null
  }

  /**
   * Initialize TensorFlow.js backend and load the segmentation model
   * Implements proper fallback chain for older devices
   */
  async initialize() {
    if (this.isInitialized) {
      return
    }

    try {
      const startTime = performance.now()

      // Initialize backend with proper fallback chain
      await this.initializeBackend()

      // Log final backend selection prominently
      const selectedBackend = tf.getBackend()
      const performanceNote =
        selectedBackend === 'webgpu'
          ? '(optimal)'
          : selectedBackend === 'webgl'
          ? '(good)'
          : '(slow fallback)'
      console.info(
        `[TF.js] Using backend: ${selectedBackend} ${performanceNote}`
      )

      // Load model
      console.info('[TF.js] Loading model from:', this.config.modelPath)
      this.model = await tf.loadGraphModel(this.config.modelPath)

      // Cold start to compile the whole network (may take a second)
      console.info('[TF.js] Warming up model...')
      const warmupTensor = tf.zeros([
        1,
        this.config.inputHeight,
        this.config.inputWidth,
        3,
      ])
      this.model.execute(warmupTensor)
      warmupTensor.dispose()

      const loadTime = performance.now() - startTime
      console.info(
        `[TF.js] Model loaded and warmed up in ${loadTime.toFixed(0)}ms`
      )

      this.isInitialized = true
    } catch (error) {
      console.error('[TF.js] Initialization failed:', error)
      throw new Error(
        `Segmentation model initialization failed: ${error.message}`
      )
    }
  }

  /**
   * Initialize TensorFlow.js backend with fallback chain
   *
   * Order: WebGPU (best performance) -> WebGL (good compatibility) -> CPU (fallback)
   * Falls back to older backends if newer ones aren't supported
   * Tests each backend with a simple operation to ensure it actually works
   */
  async initializeBackend() {
    // Try backends in order of preference: best performance to most compatible
    const backends = ['webgpu', 'webgl', 'cpu']

    for (const backendName of backends) {
      try {
        console.info(`[TF.js] Trying backend: ${backendName}...`)

        // Set backend
        const success = await tf.setBackend(backendName)
        if (!success) {
          console.warn(
            `[TF.js] Backend ${backendName} setBackend returned false`
          )
          continue
        }

        // Wait for backend to be ready
        await tf.ready()

        // Test backend with a simple operation to ensure it actually works
        const testTensor = tf.tensor2d([
          [1, 2],
          [3, 4],
        ])
        const result = testTensor.square()
        await result.data() // Force execution
        testTensor.dispose()
        result.dispose()

        this.currentBackend = backendName
        console.info(`[TF.js] Successfully initialized ${backendName} backend`)
        return
      } catch (error) {
        console.warn(`[TF.js] Backend ${backendName} failed:`, error.message)
        // Clean up and try next backend
        try {
          tf.removeBackend(backendName)
        } catch (e) {
          // Ignore cleanup errors
        }
        continue
      }
    }

    throw new Error(
      'Could not initialize any TensorFlow.js backend. Your device may not be supported.'
    )
  }

  /**
   * Get current GPU/CPU backend being used
   * @returns {string} Backend name (webgpu, webgl, or cpu)
   */
  getBackend() {
    return this.currentBackend || tf.getBackend()
  }

  /**
   * Prepare image for the ML model
   * Resizes and converts to tensor format that the model expects
   * @param {HTMLImageElement|HTMLVideoElement|HTMLCanvasElement} imageEl - Source image
   * @param {Object|number} exifDataOrOrientation - EXIF data object or orientation number
   * @param {HTMLCanvasElement} imageCanvas - Canvas to draw source image (optional)
   * @param {HTMLCanvasElement} resultCanvas - Canvas to draw results (optional)
   */
  preprocess(
    imageEl,
    exifDataOrOrientation = 1,
    imageCanvas = null,
    resultCanvas = null
  ) {
    performance.mark('start_preprocess')

    // Dispose previous tensors
    if (this.input) {
      this.input.dispose()
      this.input = null
    }
    if (this.inputImage) {
      this.inputImage.dispose()
      this.inputImage = null
    }

    // detect if browser already auto-oriented the image
    let finalSource = this.normalizeOrientation(imageEl, exifDataOrOrientation)
    this.processedSource = finalSource // Store to ensure draw matches preprocess

    if (this.debug) {
      console.log('[Segmentation] Orientation Check:', {
        exifOrientation:
          typeof exifDataOrOrientation === 'object'
            ? exifDataOrOrientation.orientation
            : exifDataOrOrientation,
        finalWidth: finalSource.width,
        finalHeight: finalSource.height,
        usingCanvas: finalSource instanceof HTMLCanvasElement,
      })
    }

    // Create tensor from processed source
    this.inputImage = tf.browser.fromPixels(finalSource)

    // Log dimensions for debugging
    this.originalHeight = this.inputImage.shape[0]
    this.originalWidth = this.inputImage.shape[1]

    // Draw to canvases if provided
    if (imageCanvas) {
      imageCanvas.height = this.originalHeight
      imageCanvas.width = this.originalWidth
      tf.browser.toPixels(this.inputImage, imageCanvas)
    }

    if (resultCanvas) {
      resultCanvas.height = this.originalHeight
      resultCanvas.width = this.originalWidth
      tf.browser.toPixels(this.inputImage, resultCanvas)
    }

    // Prepare input with letterboxing
    this.input = tf.tidy(() => {
      const image = this.inputImage

      // Calculate scaling factor
      let scalingFactor = 1
      if (this.originalHeight > this.originalWidth) {
        scalingFactor = this.config.inputHeight / this.originalHeight
      } else {
        scalingFactor = this.config.inputWidth / this.originalWidth
      }

      this.scaledOriginalHeight = Math.round(
        this.originalHeight * scalingFactor
      )
      this.scaledOriginalWidth = Math.round(this.originalWidth * scalingFactor)
      const mx = Math.max(this.scaledOriginalWidth, this.scaledOriginalHeight)

      if (this.debug) {
        console.log('Image sizes:', {
          original: [this.originalHeight, this.originalWidth],
          scaled: [this.scaledOriginalHeight, this.scaledOriginalWidth],
          scalingFactor,
        })
      }

      // Resize, pad, normalize
      return image
        .resizeBilinear([this.scaledOriginalHeight, this.scaledOriginalWidth])
        .pad([
          [
            this.scaledOriginalHeight === mx
              ? 0
              : (mx - this.scaledOriginalHeight) / 2,
            this.scaledOriginalHeight === mx
              ? 0
              : (mx - this.scaledOriginalHeight) / 2,
          ],
          [
            this.scaledOriginalWidth === mx
              ? 0
              : (mx - this.scaledOriginalWidth) / 2,
            this.scaledOriginalWidth === mx
              ? 0
              : (mx - this.scaledOriginalWidth) / 2,
          ],
          [0, 0],
        ])
        .resizeNearestNeighbor([
          this.config.inputWidth,
          this.config.inputHeight,
        ])
        .expandDims(0)
        .toFloat()
        .div(tf.scalar(255))
    })

    performance.mark('end_preprocess')
  }

  /**
   * Run model inference
   */
  runInference() {
    performance.mark('start_inference')

    // Dispose previous output
    if (this.output) {
      this.output[0].dispose()
      this.output[1].dispose()
      this.output = null
    }

    this.output = this.model.execute(this.input)

    performance.mark('end_inference')
  }

  /**
   * Postprocess model output to extract binary mask
   * Optimized implementation for YOLO11n-seg with coordinate transformation
   * Handles letterboxing offsets to ensure mask matches original image features
   *
   * @returns {tf.Tensor2D|null} Binary mask or null if no detection
   */
  async postprocess() {
    performance.mark('start_postprocess')

    const [detectionTensor, segmentationTensor] = this.output

    // Dispose previous mask
    if (this.mask) {
      this.mask.dispose()
      this.mask = null
    }

    const detections = detectionTensor.squeeze() // Shape: [37, 8400]
    const segmentationMap = segmentationTensor.squeeze() // Shape: [160, 160, 32]

    // Get confidence scores and filter by threshold
    const confidences = detections.slice(
      [this.config.xyxy, 0],
      [this.config.classes, -1]
    )

    // Find max confidence detection (optimized for highest quality candidate)
    const maxIndexTensor = confidences.argMax(1)
    const maxIndex = (await maxIndexTensor.data())[0]
    maxIndexTensor.dispose()

    const maxConfidenceTensor = confidences.gather(maxIndex, 1)
    const maxConfidence = (await maxConfidenceTensor.data())[0]
    maxConfidenceTensor.dispose()

    if (maxConfidence < this.config.confidenceThreshold) {
      if (this.debug) {
        console.warn(
          `[Segmentation] Goat not detected (conf: ${maxConfidence.toFixed(
            2
          )} < threshold ${this.config.confidenceThreshold})`
        )
      }
      detections.dispose()
      segmentationMap.dispose()
      confidences.dispose()
      performance.mark('end_postprocess')
      return null
    }

    if (this.debug) {
      console.log(
        `[Segmentation] Goat detected! Confidence: ${maxConfidence.toFixed(2)}`
      )
    }

    // Extract bounding box data (normalized model coordinates)
    const boxTensor = detections.slice([0, maxIndex], [this.config.xyxy, 1])
    const boxData = await boxTensor.data()
    this.box = new Box(Array.from(boxData))
    boxTensor.dispose()

    // Create binary mask by applying coefficients to prototypes
    this.mask = tf.tidy(() => {
      // Extract mask coefficients for the winning detection
      const maskCoeffs = detections
        .slice(
          [this.config.xyxy + this.config.classes, maxIndex],
          [this.config.numMasks, 1]
        )
        .squeeze()

      // Calculate letterbox padding offsets
      const mx = Math.max(this.scaledOriginalHeight, this.scaledOriginalWidth)
      const heightStart =
        this.scaledOriginalHeight === mx
          ? 0
          : (mx - this.scaledOriginalHeight) / 2
      const widthStart =
        this.scaledOriginalWidth === mx
          ? 0
          : (mx - this.scaledOriginalWidth) / 2

      // Compute mask via matrix multiplication: coefficients @ prototypes
      let mask = segmentationMap
        .matMul(maskCoeffs.expandDims(1))
        .squeeze()
        .expandDims(-1)
        .resizeBilinear(
          [this.config.inputHeight, this.config.inputWidth],
          false,
          true
        )
        .squeeze()
        // Crop mask to bounding box area to remove noise
        .slice(
          [this.box.topY(), this.box.topX()],
          [
            Math.min(this.box.height(), this.config.inputHeight),
            Math.min(this.box.width(), this.config.inputWidth),
          ]
        )
        // Pad back up to original input size
        .pad([
          [
            this.box.topY(),
            this.config.inputHeight - this.box.topY() - this.box.height(),
          ],
          [
            this.box.topX(),
            this.config.inputWidth - this.box.topX() - this.box.width(),
          ],
        ])
        // Remove letterbox padding to align with original image aspect ratio
        .slice(
          [heightStart, widthStart],
          [this.scaledOriginalHeight, this.scaledOriginalWidth]
        )

      // Convert probabilities to binary mask
      const binary = tf.where(
        mask.greater(0),
        tf.ones(mask.shape, 'int32'),
        tf.zeros(mask.shape, 'int32')
      )

      // Adjust bounding box coordinates for letterbox offset
      // This maps the box from model space (640x640 with padding) to image space
      if (this.scaledOriginalWidth !== mx) {
        this.box.x -= (mx - this.scaledOriginalWidth) / 2
      }
      if (this.scaledOriginalHeight !== mx) {
        this.box.y -= (mx - this.scaledOriginalHeight) / 2
      }

      return binary
    })

    detections.dispose()
    segmentationMap.dispose()
    confidences.dispose()

    performance.mark('end_postprocess')
    return this.mask
  }

  /**
   * Draw results on canvas
   *
   * @param {HTMLCanvasElement|null} canvas - Canvas to draw results
   * @param {HTMLImageElement|null} originalImage - The original image element
   */
  async draw(canvas, originalImage = null) {
    if (!this.mask || !this.box || !canvas) {
      return
    }

    performance.mark('start_draw')

    // Create overlay
    const newOverlay = tf.tidy(() => {
      const expandedMask = this.mask.expandDims(-1)
      const overlay = tf.zeros(
        [this.scaledOriginalHeight, this.scaledOriginalWidth, 4],
        'int32'
      )
      // Mask color [0, 0, 128, 150] (semi-transparent blue), background [0,0,0,0]
      return tf.where(
        expandedMask.greater(0),
        tf.tensor1d([0, 0, 128, 150], 'int32'),
        overlay
      )
    })

    // Prepare canvas dimensions (reset to original image size)
    canvas.width = this.originalWidth
    canvas.height = this.originalHeight

    const ctx = canvas.getContext('2d')

    // Draw original image first (using image element is much faster)
    if (originalImage) {
      ctx.drawImage(
        originalImage,
        0,
        0,
        this.originalWidth,
        this.originalHeight
      )
    } else if (this.inputImage) {
      await tf.browser.toPixels(this.inputImage, canvas)
    }

    // Create a temporary canvas for the mask at its small resolution
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = this.scaledOriginalWidth
    tempCanvas.height = this.scaledOriginalHeight
    await tf.browser.toPixels(newOverlay, tempCanvas)
    newOverlay.dispose()

    // Scale up the mask overlay onto the original canvas
    ctx.drawImage(tempCanvas, 0, 0, this.originalWidth, this.originalHeight)

    // Draw bounding box (scaled to original image space)
    const scaleX = this.originalWidth / this.scaledOriginalWidth
    const scaleY = this.originalHeight / this.scaledOriginalHeight

    ctx.strokeStyle = 'red'
    ctx.lineWidth = Math.max(2, 4 * (this.originalWidth / 1000))
    ctx.strokeRect(
      this.box.topX() * scaleX,
      this.box.topY() * scaleY,
      this.box.w * scaleX,
      this.box.h * scaleY
    )

    performance.mark('end_draw')
  }

  /**
   * Find and detect goat in image
   *
   * @param {HTMLImageElement|HTMLVideoElement|HTMLCanvasElement|Blob} image - Input image
   * @param {HTMLCanvasElement} imageCanvas - Canvas for source image (optional)
   * @param {HTMLCanvasElement} resultCanvas - Canvas for results (optional)
   * @param {Object} exifData - EXIF metadata including orientation
   * @returns {Object} Segmentation result with mask and metadata
   */
  async segmentGoat(
    image,
    imageCanvas = null,
    resultCanvas = null,
    exifData = { orientation: 1 }
  ) {
    if (!this.isInitialized) {
      await this.initialize()
    }

    const startTime = performance.now()

    try {
      // Convert Blob to HTMLImageElement if needed
      let imageEl = image
      if (image instanceof Blob) {
        imageEl = await this.blobToImage(image)
      }

      // Run pipeline: preprocess -> inference -> postprocess
      this.preprocess(imageEl, exifData, imageCanvas, resultCanvas)
      this.runInference()
      const mask = await this.postprocess()

      if (resultCanvas) {
        // use same processed source that was used for ML to ensure mask alignment
        await this.draw(resultCanvas, this.processedSource || imageEl)
      }

      const processingTime = performance.now() - startTime

      if (!mask || !this.box) {
        console.warn('[Segmentation] No goat detected')
        return {
          mask: null,
          maskWidth: this.config.inputWidth,
          maskHeight: this.config.inputHeight,
          boundingBox: null,
          accuracy: 0,
          originalWidth: this.originalWidth,
          originalHeight: this.originalHeight,
          processingTimeMs: processingTime,
        }
      }

      // We return the bounding box in the coordinate space of the mask (scaledOriginal)
      // This is what MeasurementService expects.
      const maskData = await mask.data()

      console.groupCollapsed('[ML] Segmentation Complete')
      console.info('Mask:', `${mask.shape[0]}x${mask.shape[1]}`)
      console.info('Bounding box (mask space):', this.box)
      console.info('Processing time:', `${processingTime.toFixed(0)}ms`)
      console.groupEnd()

      return {
        mask: mask, // Keep as tf.Tensor2D for MeasurementService
        maskData: maskData, // Uint8Array for visualization
        maskWidth: mask.shape[1],
        maskHeight: mask.shape[0],
        boundingBox: this.box, // In mask space (scaledOriginalWidth/Height)
        accuracy: 0.95,
        originalWidth: this.originalWidth,
        originalHeight: this.originalHeight,
        scaledWidth: this.scaledOriginalWidth,
        scaledHeight: this.scaledOriginalHeight,
        processingTimeMs: processingTime,
      }
    } catch (error) {
      console.error('[Segmentation] Segmentation failed:', error)
      throw new Error(`Segmentation failed: ${error.message}`)
    }
  }

  /**
   * Normalize image orientation based on EXIF tag
   *
   * IMPORTANT: PhotoWeightView.vue pre-normalizes the ML blob via drawOrientedToCanvas()
   * before calling the pipeline. When segmentationExifData.orientation === 1 is passed,
   * it means the image is already correctly oriented.
   *
   * This method simply "bakes" the image to a canvas for TensorFlow.js compatibility.
   * Complex orientation handling is done upstream in PhotoWeightView to avoid
   * double-rotation issues across different browsers and devices.
   *
   * @param {HTMLImageElement} imageEl - Source image
   * @param {Object|number} exifDataOrOrientation - EXIF data or orientation number
   * @returns {HTMLCanvasElement} Canvas with the image drawn
   */
  normalizeOrientation(imageEl, exifDataOrOrientation) {
    const orientation =
      typeof exifDataOrOrientation === 'object'
        ? exifDataOrOrientation.orientation
        : exifDataOrOrientation

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    // If orientation is 1 (normal) or not specified, just bake to canvas as-is
    // PhotoWeightView already handles orientation normalization for ML input
    if (!orientation || orientation <= 1) {
      canvas.width = imageEl.width
      canvas.height = imageEl.height
      ctx.drawImage(imageEl, 0, 0)
      return canvas
    }

    // For non-1 orientations (e.g., gallery uploads on desktop that weren't pre-normalized),
    // apply the EXIF orientation transform
    const needsSwapping = orientation > 4
    const width = needsSwapping ? imageEl.height : imageEl.width
    const height = needsSwapping ? imageEl.width : imageEl.height

    canvas.width = width
    canvas.height = height

    // Apply transformation based on standard EXIF orientation mappings
    switch (orientation) {
      case 2:
        ctx.transform(-1, 0, 0, 1, width, 0)
        break // Flip horizontal
      case 3:
        ctx.transform(-1, 0, 0, -1, width, height)
        break // Rotate 180°
      case 4:
        ctx.transform(1, 0, 0, -1, 0, height)
        break // Flip vertical
      case 5:
        ctx.transform(0, 1, 1, 0, 0, 0)
        break // Transpose
      case 6:
        ctx.transform(0, 1, -1, 0, width, 0)
        break // Rotate 90° CW
      case 7:
        ctx.transform(0, -1, -1, 0, width, height)
        break // Transverse
      case 8:
        ctx.transform(0, -1, 1, 0, 0, height)
        break // Rotate 270° CW (90° CCW)
    }

    ctx.drawImage(imageEl, 0, 0)
    return canvas
  }

  /**
   * Helper: Convert Blob to Image element
   */
  async blobToImage(blob) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)
        resolve(img)
      }
      img.onerror = reject
      img.src = URL.createObjectURL(blob)
    })
  }

  /**
   * Clean up resources
   */
  async dispose() {
    if (this.input) {
      this.input.dispose()
      this.input = null
    }
    if (this.inputImage) {
      this.inputImage.dispose()
      this.inputImage = null
    }
    if (this.mask) {
      this.mask.dispose()
      this.mask = null
    }
    if (this.output) {
      this.output[0].dispose()
      this.output[1].dispose()
      this.output = null
    }
    this.isInitialized = false
  }
}
